import { useId } from "react";

import type { BuyerBulkPresentItemDto, BuyerBulkPresentResultDto } from "@/api/buyers";
import { Button } from "@/design-system/components/ui";

import { associationsCopy as copy } from "./associationsCopy";
import type { AssociationSelection } from "./associationSelection";

/**
 * The bar above the Associated listings table while listings are ticked:
 * how many, Clear, and PRESENT ALL SELECTED (D13/D14). The look of the
 * Submissions tab's combined-intro bar.
 *
 * <p>Absent at zero. Above the per-action cap the button stays on screen
 * DISABLED with the sentence saying why beside it, wired with
 * `aria-describedby` so the reason is read with the button.</p>
 */
export function PresentSelectedBar({
  selection,
  onPresent,
  pending,
}: {
  selection: AssociationSelection;
  onPresent: () => void;
  pending: boolean;
}) {
  const reasonId = useId();
  const { count, notAllOnThisPage, blockedReason, clear } = selection;
  if (count === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid="associations-present-bar"
    >
      <span className="text-small text-onyx" data-testid="associations-present-bar-count">
        {copy.selectedCount(count)}
        {notAllOnThisPage ? ` ${copy.notAllOnThisPage}` : ""}
      </span>
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-space-2">
        {blockedReason && (
          <span
            id={reasonId}
            className="text-caption text-gray-mid"
            data-testid="associations-present-bar-reason"
          >
            {blockedReason}
          </span>
        )}
        <Button
          variant="text"
          size="sm"
          onClick={clear}
          disabled={pending}
          data-testid="associations-present-bar-clear"
        >
          {copy.clear}
        </Button>
        <Button
          size="sm"
          onClick={onPresent}
          disabled={blockedReason != null}
          loading={pending}
          aria-describedby={blockedReason ? reasonId : undefined}
          data-testid="associations-present-all"
        >
          {copy.presentAllButton(count)}
        </Button>
      </div>
    </div>
  );
}

/** The reason one skipped or failed listing gives (D15). */
export function bulkItemReason(item: BuyerBulkPresentItemDto): string {
  if (item.outcome === "Skipped" && item.existing) return copy.skippedExisting(item.existing);
  return item.reason?.trim() || (item.outcome === "Failed" ? copy.failedFallback : copy.alreadyPresented);
}

/**
 * D15 — what the last PRESENT ALL SELECTED did: the three counts, and every
 * listing that was skipped or failed BY NAME with its reason. Stays until the
 * reader dismisses it, because a toast that names twelve listings is gone
 * before it is read.
 */
export function BulkPresentResult({
  result,
  onDismiss,
}: {
  result: BuyerBulkPresentResultDto;
  onDismiss: () => void;
}) {
  const skipped = result.items.filter((i) => i.outcome === "Skipped");
  const failed = result.items.filter((i) => i.outcome === "Failed");

  return (
    <div
      className="grid gap-space-2 rounded-md border border-border-light bg-white-warm p-space-3"
      role="status"
      data-testid="associations-present-result"
    >
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <span className="text-small font-bold text-onyx" data-testid="associations-present-result-summary">
          {copy.resultSummary(result.presented, result.skipped, result.failed)}
        </span>
        <Button variant="text" size="sm" onClick={onDismiss} data-testid="associations-present-result-dismiss">
          {copy.dismissResult}
        </Button>
      </div>
      <OutcomeList heading={copy.resultSkippedHeading} items={skipped} testId="associations-present-result-skipped" />
      <OutcomeList heading={copy.resultFailedHeading} items={failed} testId="associations-present-result-failed" />
    </div>
  );
}

function OutcomeList({
  heading,
  items,
  testId,
}: {
  heading: string;
  items: BuyerBulkPresentItemDto[];
  testId: string;
}) {
  if (items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <p className="m-0 text-caption font-bold text-gray-mid">{heading}</p>
      <ul className="m-0 list-disc pl-space-4 text-small text-onyx">
        {items.map((i) => (
          <li key={i.listingId}>
            {i.listingDisplayName} — {bulkItemReason(i)}
          </li>
        ))}
      </ul>
    </div>
  );
}
